import { useEffect, useState } from "react";
import { Button, NumberInput } from "@mantine/core";
import { IconArrowRight, IconCheck, IconMessage } from "@tabler/icons-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import useAutoMessageStore from "@/store/autoMessage";
import useChangeCustomerNotifier from "@/services/autoMessage/hooks/useChangeCustomerNotifier";
import { useGetSmsPlaceHolder } from "@/services/smsPlaceHolders/hooks/useGetSmsPlaceHolder";
import MessageText, { smsPartsOf } from "@/modules/marketing/MessageText";
import page from "../selectTemplate/selectTemplate.module.css";
import classes from "./selectTime.module.css";

const fa = (value: number) => value.toLocaleString("fa-IR");
// Delay in minutes after the event (registration or invoice); the API takes 1–60.
const presets = [1, 5, 15, 30, 60];
const delayLabel = (minutes: number) => (minutes === 60 ? "۱ ساعت" : `${fa(minutes)} دقیقه`);

// Step 2 of an automatic SMS: when it goes out. The chosen text is shown as the customer will read it,
// with a way back to change it; saving turns the message on.
function SelectAutoMessageTime() {
  const { notifier, smsTemplate } = useAutoMessageStore();
  const navigate = useNavigate();
  const { changeCustomerNotifierMerchant, isLoading, isSuccess } = useChangeCustomerNotifier();
  const [minutes, setMinutes] = useState(() => Math.min(60, Math.max(1, notifier?.sendTime || 1)));
  const tags = useGetSmsPlaceHolder().data ?? [];

  useEffect(() => {
    if (isSuccess) navigate("/dashboard/profile/autoMessage");
  }, [isSuccess, navigate]);

  if (!notifier) return <Navigate to="/dashboard/profile/autoMessage" replace />;
  if (!smsTemplate?.id) return <Navigate to="/dashboard/profile/autoMessage/selectTemplate" replace />;

  const text = smsTemplate.text ?? "";
  const submit = () =>
    changeCustomerNotifierMerchant({ isActive: true, smsTemplateId: smsTemplate.id, notifierType: notifier.type, sendTime: minutes });

  return (
    <main className={page.page} dir="rtl">
      <header className={page.bar}>
        <Link to="/dashboard/profile/autoMessage/selectTemplate" className={page.back} aria-label="مرحله قبل">
          <IconArrowRight size={22} />
        </Link>
        <div className={page.barTitle}>
          <strong>زمان ارسال</strong>
          <span>{notifier.typeDisplay || "پیامک خودکار"}</span>
        </div>
        <span className={page.step}>مرحله ۲ از ۲</span>
      </header>
      <div className={page.progress}><span className={classes.full} /></div>

      <section className={page.body}>
        <div className={page.intro}>
          <h1>چند دقیقه بعد فرستاده شود؟</h1>
          <p>زمان از لحظه‌ی {notifier.typeDisplay ? `«${notifier.typeDisplay}»` : "رویداد"} حساب می‌شود.</p>
        </div>

        <div className={classes.presets} role="radiogroup" aria-label="زمان ارسال">
          {presets.map((value) => (
            <button key={value} type="button" role="radio" aria-checked={minutes === value} className={classes.preset} onClick={() => setMinutes(value)}>
              {delayLabel(value)}
            </button>
          ))}
        </div>
        <NumberInput
          label="یا زمان دلخواه (دقیقه)"
          value={minutes}
          min={1}
          max={60}
          clampBehavior="strict"
          allowDecimal={false}
          allowNegative={false}
          onChange={(value) => setMinutes(Math.min(60, Math.max(1, Number(value) || 1)))}
          styles={{ input: { direction: "ltr", textAlign: "left" } }}
        />

        <div className={classes.preview}>
          <div className={classes.previewHead}>
            <span className={classes.icon}><IconMessage size={18} /></span>
            <div>
              <strong>{smsTemplate.title || "متن انتخاب‌شده"}</strong>
              <span>{fa(smsPartsOf(text, tags))} پیامک · {delayLabel(minutes)} بعد</span>
            </div>
            <Link to="/dashboard/profile/autoMessage/selectTemplate" className={classes.change}>تغییر متن</Link>
          </div>
          <div className={classes.bubble}><MessageText text={text} tags={tags} /></div>
        </div>
      </section>

      <footer className={page.footer}>
        <div className={page.footerInfo}>
          <span>پس از ثبت، این پیامک خودکار روشن می‌شود.</span>
        </div>
        <Button size="md" color="violet" fullWidth loading={isLoading} onClick={submit} leftSection={<IconCheck size={18} />}>
          ثبت و فعال‌سازی
        </Button>
      </footer>
    </main>
  );
}

export default SelectAutoMessageTime;
